import { useCallback } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { CurveData, Dvh } from '~/domain/patient/dvh';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { buildDvhPoints } from '~/utils/dvhCurve';
import { dicomColorToRgbString } from '~/utils/color';
import type { RoiId } from '~/domain/changes/ids';

// New return type for DVH listings
export interface RoiDvhEntry {
    roiId: RoiId;
    name: string;
    dvh: Dvh;
}

/**
 * Hook to access DVH information either as CurveData (per ROI) or raw Dvh objects.
 *
 * The structures are those of the active structure set in the Environment
 * that holds its contour edits (the edit target), so every DVH and dose
 * statistic reflects the geometry the user sees and edits. When no such
 * Environment resolves, or it holds a structure set other than the active
 * one, every reader returns nothing rather than values computed from
 * another copy of the structures. The DVH cache keys on each structure's
 * contour version, so an edited structure is recomputed on its next read;
 * `environment` is returned so a caller that holds computed curves can
 * watch those versions and read again.
 *
 * Reads the display scale from the per-dose display store and extracts it
 * as a bare number, so the DVH work re-runs only when the scale changes —
 * not when other display fields (colorwash range, normalization) change
 * during interactions like colorwash slider drags.
 */
export default function useDvh() {
    const { environment, structureSet: activeStructureSet } =
        useActiveEditTarget();
    const boundStructureSet = environment?.structureSet;
    const structureSet =
        boundStructureSet &&
        boundStructureSet.sopInstanceUID === activeStructureSet?.dicomUid
            ? boundStructureSet
            : undefined;
    const currentDose = useCurrentDose();
    const { settings } = useDoseDisplaySettings(currentDose?.id ?? null);
    const doseScale = settings.doseScale;
    // The dose grid's global maximum, on the same display scale as the
    // curve. It is the ceiling for every plotted dose — see buildDvhPoints.
    // `doseMaxGy` stays on the entity: it is a property of the dose grid,
    // not a display setting, so it did not move to the display store.
    const gridMaxGy = (currentDose?.doseMaxGy ?? 0) * doseScale;

    const toCurveData = useCallback(
        (roi: RegionOfInterest, dvh: Dvh): CurveData => {
            return {
                data: buildDvhPoints(dvh, gridMaxGy, doseScale),
                color: dicomColorToRgbString(roi.colorRgb8),
                name: roi.name,
                visible: roi.isVisible,
                doseMaxGy: dvh.doseMaxGy * doseScale,
                doseMinGy: dvh.doseMinGy * doseScale,
                doseMeanGy: dvh.doseMeanGy * doseScale,
                roiId: roi.roiId,
                structureVolumeCm3: dvh.structureVolumeCm3,
            };
        },
        [doseScale, gridMaxGy]
    );

    const getAllDvhs = useCallback(async (): Promise<RoiDvhEntry[]> => {
        if (!currentDose || !structureSet) {
            return [];
        }
        const rois = structureSet.structures;
        const dvhs = await Promise.all(
            rois.map((r) => currentDose.getCalculatedDvh(r))
        );
        return dvhs.map((d, i) => ({
            roiId: rois[i].roiId,
            name: rois[i].name,
            dvh: d,
        }));
    }, [currentDose, structureSet]);

    const getDvhForRoi = useCallback(
        async (roiId: RoiId): Promise<CurveData | undefined> => {
            if (!structureSet || !currentDose) {
                return undefined;
            }
            const roi = structureSet.getRoi(roiId);
            if (!roi) {
                return undefined;
            }
            const dvh = await currentDose.getCalculatedDvh(roi);
            return dvh ? toCurveData(roi, dvh) : undefined;
        },
        [structureSet, currentDose, toCurveData]
    );

    const getVisibleRoiDvhCurves = useCallback(async (): Promise<
        CurveData[]
    > => {
        if (!structureSet || !currentDose) {
            return [];
        }
        const rois = structureSet.structures.filter((s) => s.isVisible);
        const dvhs = await Promise.all(
            rois.map((r) => currentDose.getCalculatedDvh(r))
        );
        return dvhs.map((dvh, i) => toCurveData(rois[i], dvh));
    }, [structureSet, currentDose, toCurveData]);

    return {
        environment,
        getAllDvhs,
        getDvhForRoi,
        getVisibleRoiDvhCurves,
        toCurveData,
    };
}
